// Når kontoret godkender eller tilføjer fast fri: bookinger, der allerede ligger i den faste fri, og forslag til,
// hvem de kan flyttes til.
//
// Regler:
//  - Berørt: kommende aktiv booking (afventer/godkendt) hos steameren, der rører reglen. En særlig dag den dato går forud
//    for den faste fri (som i fastFri.ts), så den booking er ikke berørt.
//  - Forslag: en anden steamer, der er ledig på samme dato og tid – samme regler som Smartplan (ledige.ts): på arbejde,
//    ikke fravær eller fast fri, ingen overlap, pause/køretid til nabo-opgaverne, zone, ydelse og max opgaver pr. dag.
//  - Bedste forslag først: mindst ekstra kørsel, så færrest opgaver den dag, så navn.
//  - Flere bookinger på samme dag: forslagene regnes i rækkefølge, og hver bookings første forslag tæller som en opgave
//    hos den steamer, så "Flyt alle til forslag" aldrig giver to bookinger til den samme på samme tid.
//  - Flytningen er "skift steamer på samme tid" (retBooking med steamer_id): kunden får ingen besked.
import { iFastFri, type FastFri } from './fastFri.ts';
import { kandidaterForSteamer, type DagsOpgave, type Regler } from './ledige.ts';
import type { Arbejdstid, BookingStatus, Fravaer, SaerligDag, Steamer, Zone } from './typer.ts';

/** De statusser, der kan flyttes til en anden steamer. */
export const BEROERT_STATUS: BookingStatus[] = ['afventer', 'godkendt'];

export type BeroertBooking = {
  id: number; dato: string; start: number; slut: number; steamer_id: string;
  postnr: string; ydelse_id: string; ydelse: string; fornavn: string;
};
export type FlytForslag = { steamer_id: string; navn: string; ekstraKoersel: number; opgaver: number };
export type BeroertMedForslag = BeroertBooking & { forslag: FlytForslag[] };

export type FlytData = {
  steamere: Steamer[]; arbejdstider: Arbejdstid[]; fravaer: Fravaer[]; saerligeDage: SaerligDag[]; lukkedage: string[];
  zoner: Zone[]; regler: Regler;
  /** Alle aktive opgaver med tid på de berørte datoer (også de berørte selv). */
  opgaver: (DagsOpgave & { dato: string })[];
};

/** Rører bookingen reglen? (Ugedag, tidsrum og særlig dag som i fastFri.ts.) */
export function iRegel(steamer_id: string, regel: FastFri, b: { dato: string; start: number | null; slut: number | null }, saerligeDage: SaerligDag[] = []): boolean {
  if (b.start == null || b.slut == null) return false;
  return !!iFastFri({ id: steamer_id, fastFri: [regel] }, b.dato, b.start, b.slut, saerligeDage);
}

/** Forslag pr. booking (bedste først), regnet i rækkefølge efter dato og tid. */
export function flytForslag(beroerte: BeroertBooking[], d: FlytData): BeroertMedForslag[] {
  const opgaver = d.opgaver.map((o) => ({ ...o }));
  const navn = (id: string) => d.steamere.find((s) => s.id === id)?.navn ?? '';
  return [...beroerte].sort((a, b) => a.dato.localeCompare(b.dato) || a.start - b.start).map((b) => {
    const dagens = opgaver.filter((o) => o.dato === b.dato);
    const antal = (sid: string) => dagens.filter((o) => o.steamer_id === sid && o.id !== b.id).length;
    const forslag = d.steamere
      .filter((s) => s.aktiv && s.id !== b.steamer_id)
      .flatMap((s) => kandidaterForSteamer({
        steamer: s, dato: b.dato, arbejdstider: d.arbejdstider, fravaer: d.fravaer, opgaver: dagens, postnr: b.postnr,
        varighed: b.slut - b.start, kunStart: b.start, ignorerId: b.id, saerligeDage: d.saerligeDage, lukkedage: d.lukkedage,
        zoner: d.zoner, ydelse_id: b.ydelse_id, regler: d.regler,
      }))
      .map((k) => ({ steamer_id: k.steamer_id, navn: navn(k.steamer_id), ekstraKoersel: k.ekstraKoersel, opgaver: antal(k.steamer_id) }))
      .sort((x, y) => x.ekstraKoersel - y.ekstraKoersel || x.opgaver - y.opgaver || x.navn.localeCompare(y.navn, 'da'));
    // Det første forslag "optager" tiden, så næste booking ikke får den samme steamer på samme tid.
    if (forslag[0]) {
      const o = opgaver.find((x) => x.id === b.id);
      if (o) o.steamer_id = forslag[0].steamer_id;
      else opgaver.push({ id: b.id, steamer_id: forslag[0].steamer_id, start: b.start, slut: b.slut, postnr: b.postnr, dato: b.dato });
    }
    return { ...b, forslag };
  });
}
